import { useMemo } from 'react'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineGlobeIcon from 'LuxKit/Icons/line/LineGlobeIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'
import { partitionBy } from 'lib/array/arrayUtils'
import { getDistinctGrowthInclusions } from 'lib/offer/growthOfferTile'
import GrowthInclusionList from './GrowthInclusionList'

interface Props {
  variation: App.Tours.TourV2OfferVariation
  isLuxPlusEnabled: boolean
}

/**
 * Compact counterpart of components/OfferPage/TourV2/TourV2HighlightedInclusions.tsx.
 * Preserve its member-bonus eligibility and bonus/flash/standard priority while
 * rendering the Growth tile's single-line inclusion preview. Figma explicitly calls
 * for tour facts when inclusions are unavailable, so that fallback uses the existing
 * useTourV2VariationDetails formatter rather than constructing new duration/location copy.
 */
function GrowthTourInclusions({ variation, isLuxPlusEnabled }: Props) {
  const details = useTourV2VariationDetails(variation)
  // Match the offer page: member bonuses, flash inclusions, then standard inclusions.
  const inclusions = useMemo(() => {
    const [flash, nonFlash] = partitionBy(variation.inclusionItems, inclusion => !!inclusion.isFlash)
    const eligible = isLuxPlusEnabled ? nonFlash : nonFlash.filter(inclusion => !inclusion.isLuxPlusBonus)
    const [bonus, standard] = partitionBy(eligible, inclusion => !!inclusion.isLuxPlusBonus)
    return getDistinctGrowthInclusions([...bonus, ...flash, ...standard])
  }, [variation.inclusionItems, isLuxPlusEnabled])

  if (inclusions.length) {
    return <GrowthInclusionList>
      {inclusions.map(inclusion => <OfferInclusionItem
        key={inclusion.id}
        inclusion={inclusion}
        size="small"
        lineClamp={1}
        type={inclusion.isLuxPlusBonus ? 'lux-plus' : 'default'}
        colour={inclusion.isLuxPlusBonus ? 'lux-plus' : undefined}
      />)}
    </GrowthInclusionList>
  }

  return <GrowthInclusionList>
    {!!details.daysCount && <BodyTextBlock key="days" variant="small" lineClamp={1} startIcon={<LineCalendarIcon size="XS" />}>{details.durationInDaysCount}</BodyTextBlock>}
    {details.countriesCount && <BodyTextBlock key="countries" variant="small" lineClamp={1} startIcon={<LineGlobeIcon size="XS" />}>{details.countriesCount}</BodyTextBlock>}
    {details.placesCount && <BodyTextBlock key="places" variant="small" lineClamp={1} startIcon={<LineMapMarkerIcon size="XS" />}>{details.placesCount}</BodyTextBlock>}
  </GrowthInclusionList>
}

export default GrowthTourInclusions
